import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { Box, Collapse, IconButton, LinearProgress, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { AppState } from '@store/store';
import React, { type ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import type { ITabConfig } from '../MvpTabContainer';

interface ContentHeaderProps {
    title: React.ReactNode;
    subTitle?: React.ReactNode;
    caption?: React.ReactNode;
    icon?: React.ReactNode;
    inDialog?: boolean;
    bigLeftBox?: JSX.Element;
    buttons?: ReactNode;
    dashboardSwitcher?: ReactNode;
    styleType?: 'grid' | 'flex';
    children?: ReactNode;
    link?: string;
    tabs?: ITabConfig[];
    onDrag?: (e: React.DragEvent<HTMLDivElement>) => void;
}

/**
 * Component for displaying the header of a content section.
 *
 * @param {ContentHeaderProps} props - The props object for configuring the content header.
 * @returns {JSX.Element} - The JSX element representing the content header.
 */
function ContentHeader(props: ContentHeaderProps) {
    const theme = useTheme();
    const offline = useSelector((state: AppState) => state.client.offline);
    const loading = useSelector((state: AppState) => state.client.loading);
    const styleType = props.styleType ? props.styleType : 'grid';
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [infoExpanded, setInfoExpanded] = useState(false);

    // Don't render anything if no content is provided
    if (!props.title && !props.subTitle && !props.buttons && !props.dashboardSwitcher && !props.children) {
        return null;
    }

    return (
        <Box
            sx={{
                userSelect: 'none',
                mt: { xs: 1.875, sm: 4 },
                mb: 1.5,
                position: 'relative',
                // Futuristic Container für gesamten ContentHeader
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                borderRadius: 3,
                p: { xs: 2, sm: 3 },
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                backdropFilter: 'blur(10px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
                overflow: 'hidden',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, transparent 50%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                    pointerEvents: 'none',
                },
                // Futuristic accent line
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: 2,
                    background: `linear-gradient(90deg, transparent 0%, ${theme.palette.primary.main} 50%, transparent 100%)`,
                    opacity: 0.6,
                },
            }}
        >
            {/* Mobile DashboardSwitcher über ContentHeader */}
            {isMobile && props.dashboardSwitcher && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start', position: 'relative', zIndex: 1 }}>
                    {props.dashboardSwitcher}
                </Box>
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: 1,
                    mb: 2,
                }}
            >
                {/* Linke Seite: Icon + Caption + Titel + Untertitel */}
                <Stack direction="row" sx={{ flexGrow: 1, minWidth: 0, alignItems: 'flex-start', gap: 2 }}>
                    {props.icon && (
                        <Box
                            sx={{
                                bgcolor: theme.palette.primary.main,
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                alignSelf: 'flex-start',
                                width: 40,
                                height: 40,
                                borderRadius: '8px 8px 0px 8px',
                                '& svg': { fontSize: '1.5rem' },
                            }}
                        >
                            {props.icon}
                        </Box>
                    )}
                    <Stack direction="column" sx={{ flexGrow: 1, minWidth: 0, gap: 0.25 }}>
                        {props.caption && (
                            <Typography
                                sx={{
                                    fontFamily: 'Bricolage Grotesque, Inter, Arial, sans-serif !important',
                                    fontWeight: 400,
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    lineHeight: 1,
                                    color: theme.palette.text.secondary,
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                {props.caption}
                            </Typography>
                        )}
                        {props.title && (
                            <Box onDrag={props.onDrag}>
                                {props.link ? (
                                    <Link
                                        to={props.link}
                                        style={{
                                            textDecoration: 'none',
                                            color: theme.palette.text.primary,
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        <Typography
                                            variant="h3"
                                            sx={{
                                                color: 'inherit',
                                                fontWeight: 500,
                                                '&:hover': {
                                                    color: theme.palette.primary.main,
                                                    transform: 'translateY(-1px)',
                                                },
                                            }}
                                        >
                                            {props.title}
                                        </Typography>
                                    </Link>
                                ) : (
                                    <Typography
                                        variant="h3"
                                        sx={{
                                            fontWeight: 500,
                                            color: theme.palette.text.primary,
                                        }}
                                    >
                                        {props.title}
                                    </Typography>
                                )}
                            </Box>
                        )}
                        {props.subTitle && (
                            <Typography
                                sx={{
                                    fontFamily: 'Bricolage Grotesque, Inter, Arial, sans-serif !important',
                                    fontWeight: 400,
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    lineHeight: 1.4,
                                    color: theme.palette.text.secondary,
                                    mt: 0.5,
                                    opacity: 0.8,
                                }}
                            >
                                {props.subTitle}
                            </Typography>
                        )}
                    </Stack>
                </Stack>

                {/* Rechte Seite: Dashboard Switcher + Buttons */}
                {(props.dashboardSwitcher || props.buttons) && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            ml: 2,
                            '& .MuiIconButton-root, & .MuiButton-root': {
                                color: theme.palette.text.secondary,
                                borderRadius: '50%',
                                minWidth: '40px',
                                minHeight: '40px',
                                width: '40px',
                                height: '40px',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                    color: theme.palette.primary.main,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    transform: 'scale(1.05)',
                                },
                                '&:focus-visible': {
                                    outline: `2px solid ${theme.palette.primary.main}`,
                                    outlineOffset: 2,
                                },
                            },
                        }}
                    >
                        {!isMobile && props.dashboardSwitcher}
                        {props.buttons && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row-reverse',
                                    alignItems: 'center',
                                    flexWrap: 'wrap',
                                }}
                            >
                                {props.buttons}
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {props.children && (
                <>
                    {isMobile && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                            <IconButton
                                onClick={() => setInfoExpanded(!infoExpanded)}
                                sx={{
                                    color: theme.palette.text.secondary,
                                    '&:hover': {
                                        color: theme.palette.primary.main,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    },
                                }}
                            >
                                {infoExpanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                        </Box>
                    )}
                    <Collapse in={!isMobile || infoExpanded}>
                        <Box
                            sx={{
                                ...(styleType === 'flex'
                                    ? {
                                          display: 'flex',
                                          flexWrap: 'wrap',
                                          justifyContent: 'space-between',
                                          gap: theme.spacing(2, 3),
                                      }
                                    : {
                                          display: 'grid',
                                          gap: theme.spacing(2, 3),
                                          gridTemplateColumns: isMobile
                                              ? 'repeat(2, 1fr)'
                                              : 'repeat(auto-fit, minmax(200px, 1fr))',
                                      }),

                                mb: 2,
                                boxSizing: 'border-box',
                                position: 'relative',
                                zIndex: 1,
                                color: theme.palette.text.primary,
                                '& .info-data': {
                                    ml: 3.5,
                                },
                            }}
                        >
                            {props.children}
                        </Box>
                    </Collapse>
                </>
            )}

            {/* Tab Navigation */}
            {props.tabs && props.tabs.length > 0 && (
                <Box
                    sx={{
                        mt: 2,
                        position: 'relative',
                        zIndex: 1,
                        borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        pt: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            '& a': {
                                px: 2,
                                py: 1,
                                borderRadius: 2,
                                textDecoration: 'none',
                                color: theme.palette.text.secondary,
                                fontSize: '0.875rem',
                                fontWeight: 400,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    color: theme.palette.primary.main,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                },
                                '&.active': {
                                    color: theme.palette.primary.main,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.15),
                                    fontWeight: 500,
                                },
                            },
                        }}
                    >
                        {props.tabs.map((tab) => (
                            <Link
                                key={tab.link}
                                to={tab.link}
                                className={
                                    window.location.pathname === tab.link ||
                                    (window.location.pathname.endsWith('/dashboard') &&
                                        tab.link.endsWith('/dashboard')) ||
                                    (!window.location.pathname.includes('/attachments') &&
                                        tab.link.endsWith('/dashboard'))
                                        ? 'active'
                                        : ''
                                }
                            >
                                {tab.title}
                            </Link>
                        ))}
                    </Box>
                </Box>
            )}

            {loading && !props.inDialog && <LoadingBar />}
            {offline && (
                <Box
                    sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
                        border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        zIndex: 1,
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{
                            color: theme.palette.error.main,
                            fontWeight: 600,
                        }}
                    >
                        Offline!
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

export default ContentHeader;

/**
 * Futuristic loading bar component inspired by onboarding design.
 *
 * @returns {JSX.Element} The loading bar component.
 */
export function LoadingBar() {
    const theme = useTheme();
    const CONST_MAX_LOADING = 98;
    const [progress, setProgress] = React.useState(0);
    const [buffer, setBuffer] = React.useState(10);
    const [show, setShow] = useState(false);

    useEffect(() => {
        setTimeout(() => {
            setShow(true);
        }, 100);
    }, []);

    const progressRef = React.useRef(() => {});
    React.useEffect(() => {
        progressRef.current = () => {
            if (progress > CONST_MAX_LOADING) {
                setProgress(CONST_MAX_LOADING);
                setBuffer(99);
            } else if (progress < CONST_MAX_LOADING * 0.25) {
                const diff2 = Math.abs(Math.random() * 20);
                setProgress(progress + 10);
                setBuffer(progress + diff2);
            } else if (progress < CONST_MAX_LOADING * 0.5) {
                const diff2 = Math.abs(Math.random() * 20);
                setProgress(progress + 2);
                setBuffer(progress + diff2);
            } else if (progress < CONST_MAX_LOADING * 0.7) {
                const diff2 = Math.abs(Math.random() * 10);
                setProgress(progress + 0.75);
                setBuffer(progress + diff2);
            } else if (progress < CONST_MAX_LOADING) {
                const diff2 = Math.abs(Math.random() * 5);
                setProgress(progress + 0.2);
                setBuffer(progress + diff2);
            }
        };
    });

    React.useEffect(() => {
        const timer = setInterval(() => {
            progressRef.current();
        }, 50);

        return () => {
            clearInterval(timer);
        };
    }, []);

    if (!show) return null;

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1000,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                backdropFilter: 'blur(10px)',
                borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                userSelect: 'none',
            }}
        >
            <LinearProgress
                variant="buffer"
                value={progress}
                valueBuffer={buffer}
                sx={{
                    height: 4,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    '& .MuiLinearProgress-bar': {
                        background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        boxShadow: `0 0 10px ${alpha(theme.palette.primary.main, 0.5)}`,
                    },
                    '& .MuiLinearProgress-bar2Buffer': {
                        background: alpha(theme.palette.primary.main, 0.3),
                    },
                }}
            />
        </Box>
    );
}

// Re-export ITabConfig for backward compatibility
export type { ITabConfig };
